import React, { useEffect, useRef, useState } from 'react';
import { siftApiClient } from '../../api/siftApiClient';
import type { SalesClientSearchResult, SalesParentSearchResult } from '../../api/types';
import './SalesClientSearch.css';

interface SalesClientSearchProps {
  /**
   * The client filter values. A token is either a client id (one rooftop), a
   * client NAME (every rooftop called that), or a dealer-group PARENT name
   * (every client rolling up to it) — the backend matches all three columns.
   */
  selected: string[];
  onChange: (next: string[]) => void;
}

const MIN_LEN = 2;
const DEBOUNCE_MS = 300;

/** Every client id in the book is numeric (measured: 40,805 of 40,805). Only
 *  used to label a token whose name was never looked up. */
const looksLikeId = (token: string) => /^\d+$/.test(token);

/**
 * Client search for Sales View, replacing the open text box. The box matched the
 * same columns this does, but a rep could not see WHAT it had matched, could not
 * hold two clients at once, and had no way to tell a dealer group from a rooftop
 * that happens to share its name.
 *
 * Modelled on Client View's ClientBoardSearch, with two deliberate differences:
 * there is no VIN group, because Sales View has no VIN list or drawer to open
 * one in; and it searches the SALES book, not the seller snapshot, so it can
 * only offer clients the board actually holds a row for.
 *
 * Selections live inside the panel rather than as chips under the box: the
 * filter row is fixed-height, and a growing chip list would push the board down.
 */
export const SalesClientSearch: React.FC<SalesClientSearchProps> = ({ selected, onChange }) => {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState<SalesClientSearchResult[]>([]);
  const [parents, setParents] = useState<SalesParentSearchResult[]>([]);
  // id -> client name, so a specific pick reads as a name rather than a number.
  const [labels, setLabels] = useState<Record<string, string>>({});
  // Group tokens, so the Selected list can say "N clients" rather than showing a
  // group name as if it were one dealer.
  const [parentTokens, setParentTokens] = useState<Record<string, number>>({});
  const abortRef = useRef<AbortController | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const trimmed = q.trim();
    if (trimmed.length < MIN_LEN) {
      setClients([]); setParents([]); setLoading(false);
      abortRef.current?.abort();
      return;
    }
    const t = setTimeout(() => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);
      siftApiClient.searchSalesClients(trimmed, 8, controller.signal)
        .then((res) => {
          if (controller.signal.aborted) return;
          setParents(res.parents);
          setClients(res.clients);
          setLoading(false);
        })
        .catch((err) => {
          if (controller.signal.aborted || err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
          setClients([]); setParents([]); setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDocClick);
    return () => document.removeEventListener('pointerdown', onDocClick);
  }, []);

  const toggle = (token: string, label?: string) => {
    if (!token) return;
    if (label) setLabels((m) => ({ ...m, [token]: label }));
    onChange(selected.includes(token) ? selected.filter((s) => s !== token) : [...selected, token]);
    setQ('');
  };

  /** Same token list as a client pick — the backend also matches the parent
   *  column — but remembered as a group so the Selected list can say so. */
  const toggleParent = (name: string, clientCount: number) => {
    if (!name) return;
    setParentTokens((m) => ({ ...m, [name]: clientCount }));
    onChange(selected.includes(name) ? selected.filter((s) => s !== name) : [...selected, name]);
    setQ('');
  };

  const describe = (token: string): { text: string; hint: string; kind: 'parent' | 'client' } => {
    const groupSize = parentTokens[token];
    if (groupSize != null) {
      return { text: token, hint: `${groupSize.toLocaleString()} clients in group`, kind: 'parent' };
    }
    if (labels[token]) return { text: labels[token], hint: `#${token}`, kind: 'client' };
    if (looksLikeId(token)) return { text: `Client #${token}`, hint: '', kind: 'client' };
    return { text: token, hint: 'all clients with this name', kind: 'client' };
  };

  const searching = q.trim().length >= MIN_LEN;
  const placeholder = selected.length > 0
    ? `${selected.length} client filter${selected.length === 1 ? '' : 's'}`
    : 'Search clients · groups · client #';

  return (
    <div className="scs-wrap" ref={wrapRef}>
      <label className="scs-label">Client</label>
      <div className={`scs-field ${selected.length > 0 ? 'has-selection' : ''}`}>
        <input
          type="text"
          className="scs-input"
          placeholder={placeholder}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          aria-label="Search clients by name, client number, or dealer group"
        />
        {(q || selected.length > 0) && (
          <button
            type="button"
            className="scs-clear"
            onClick={() => { setQ(''); onChange([]); setOpen(false); }}
            aria-label="Clear client filter"
            title="Clear client filter"
          >
            ✕
          </button>
        )}
      </div>
      {open && (
        <div className="scs-results" role="listbox">
          {selected.length > 0 && (
            <div className="scs-group">
              <div className="scs-group-head">
                <span>Selected · {selected.length}</span>
                <button type="button" className="scs-clear-all" onClick={() => onChange([])}>Clear all</button>
              </div>
              {selected.map((token) => {
                const { text, hint, kind } = describe(token);
                return (
                  <button
                    key={`sel:${token}`}
                    type="button"
                    className="scs-item"
                    onClick={() => toggle(token)}
                    title={`Remove ${text}`}
                  >
                    <span className="scs-check">✓</span>
                    <span className="scs-item-body">
                      <span className="scs-item-name">
                        {kind === 'parent' && <span className="scs-badge">Group</span>}
                        {text}
                      </span>
                      {hint && <span className="scs-item-meta">{hint}</span>}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {searching && loading && <div className="scs-state">Searching…</div>}

          {searching && !loading && clients.length === 0 && parents.length === 0 && (
            <div className="scs-state">Nothing in your book matches “{q.trim()}”.</div>
          )}

          {/* Dealer groups first: picking one selects every client under it, which
              is a much wider action than picking a single rooftop, so it must not
              look like just another client row. */}
          {searching && !loading && parents.length > 0 && (
            <div className="scs-group">
              <div className="scs-group-head"><span>Dealer groups (parent)</span></div>
              {parents.map((p) => (
                <button
                  key={`p:${p.client_parent_name}`}
                  type="button"
                  className="scs-item"
                  onClick={() => toggleParent(p.client_parent_name, p.clients)}
                  title={`Filter to all ${p.clients} clients under ${p.client_parent_name}`}
                >
                  <span className="scs-check">{selected.includes(p.client_parent_name) ? '✓' : ''}</span>
                  <span className="scs-item-body">
                    <span className="scs-item-name">
                      <span className="scs-badge">Group</span>
                      {p.client_parent_name}
                    </span>
                    <span className="scs-item-meta">
                      {p.clients.toLocaleString()} client{p.clients === 1 ? '' : 's'}
                      {p.at_risk > 0 ? ` · ${p.at_risk.toLocaleString()} need attention` : ''}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}

          {searching && !loading && clients.length > 0 && (
            <div className="scs-group">
              <div className="scs-group-head"><span>Clients (individual)</span></div>
              {clients.map((c) => {
                const name = c.client_name || '';
                const shared = (c.name_count ?? 1) > 1;
                return (
                  <div key={c.client_id} className="scs-row">
                    <button
                      type="button"
                      className="scs-item"
                      onClick={() => toggle(c.client_id, name)}
                      title={name ? `Filter to ${name} (#${c.client_id})` : undefined}
                    >
                      <span className="scs-check">{selected.includes(c.client_id) ? '✓' : ''}</span>
                      <span className="scs-item-body">
                        <span className="scs-item-name">
                          {c.tier === 'URGENT' || c.tier === 'HIGH' ? (
                            <span className={`scs-tier scs-tier-${c.tier.toLowerCase()}`}>{c.tier}</span>
                          ) : null}
                          {name || `Client ${c.client_id}`}
                        </span>
                        {/* The rep is here because two rooftops can share a name;
                            without an owner and a market center they are
                            indistinguishable rows. */}
                        <span className="scs-item-meta">
                          #{c.client_id}
                          {c.client_parent_name ? ` · ${c.client_parent_name}` : ''}
                          {c.territory_market_center ? ` · ${c.territory_market_center}` : ''}
                          {c.cse_territory_owner ? ` · ${c.cse_territory_owner}` : ''}
                        </span>
                      </span>
                    </button>
                    {shared && name && (
                      <button
                        type="button"
                        className="scs-all-btn"
                        onClick={() => toggle(name)}
                        title={`Filter to all ${c.name_count} clients named ${name}`}
                      >
                        All {c.name_count}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {!searching && selected.length === 0 && (
            <div className="scs-state">Type at least {MIN_LEN} characters to search.</div>
          )}
        </div>
      )}
    </div>
  );
};

export default SalesClientSearch;
